import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CalendarDays, Plus, Trash2, ChevronLeft, ChevronRight } from "lucide-react";

const MONTH_NAMES = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const DOW = ["PZT", "SAL", "ÇAR", "PER", "CUM", "CMT", "PAZ"];
const TYPES = ["Deneme Sınavı", "Yazılı", "Zirve", "Diğer"];
const TYPE_COLORS = {
  "Deneme Sınavı": "bg-indigo-100 text-indigo-700 border-indigo-200",
  "Yazılı": "bg-amber-100 text-amber-700 border-amber-200",
  "Zirve": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Diğer": "bg-slate-100 text-slate-600 border-slate-200",
};

export default function SinavTakvimi() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0-11
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", date: "", type: "Deneme Sınavı", grade: 0 });

  const load = () => api.get("/exam-calendar").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name.trim()) return toast.error("Sınav adı gerekli");
    if (!form.date) return toast.error("Tarih seçin");
    try {
      await api.post("/exam-calendar", { name: form.name.trim(), date: form.date, type: form.type, grade: form.grade || null });
      toast.success("Takvime eklendi ✓");
      setForm({ name: "", date: "", type: "Deneme Sınavı", grade: 0 });
      const [y, m] = form.date.split("-").map(Number);
      setYear(y); setMonth(m - 1);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu sınav takvimden silinsin mi?")) return;
    try { await api.delete(`/exam-calendar/${id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const prevMonth = () => { if (month === 0) { setYear(year - 1); setMonth(11); } else setMonth(month - 1); };
  const nextMonth = () => { if (month === 11) { setYear(year + 1); setMonth(0); } else setMonth(month + 1); };

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7; // Pazartesi = 0

  const byDate = useMemo(() => {
    const map = {};
    items.forEach((it) => { (map[it.date] = map[it.date] || []).push(it); });
    return map;
  }, [items]);

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const dateKey = (d) => `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const isToday = (d) => year === today.getFullYear() && month === today.getMonth() && d === today.getDate();

  const inp = "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100";

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-100 text-rose-600"><CalendarDays className="h-6 w-6" /></div>
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Sınav Takvimi</h1>
          <p className="text-slate-500">{isAdmin ? "Ay bazlı sınavları elle girin — tüm öğrenci, veli ve koçlar anında görür." : "Okul geneli sınav programı — yaklaşan sınavlarınızı takip edin."}</p>
        </div>
      </div>

      {isAdmin && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500">Sınav Adı</label>
              <input data-testid="cal-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="örn. 1. Dönem Genel Deneme" className={inp} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500">Tarih</label>
              <input data-testid="cal-date-input" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className={inp} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500">Tür</label>
              <select data-testid="cal-type-select" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} className={inp}>
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500">Alan (Ops.)</label>
              <select data-testid="cal-grade-select" value={form.grade} onChange={(e) => setForm((f) => ({ ...f, grade: Number(e.target.value) }))} className={inp}>
                <option value={0}>Tümü</option>
                {[1, 2, 3, 4, 5, 6, 7].map((g) => <option key={g} value={g}>{g}. Sınıf</option>)}
              </select>
            </div>
          </div>
          <button data-testid="cal-add-btn" onClick={add} className="mt-4 flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 font-bold text-white transition hover:bg-indigo-700 active:scale-95">
            <Plus className="h-5 w-5" /> Takvime Ekle
          </button>
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <h3 className="flex items-center gap-2 font-display text-xl font-bold text-slate-900" data-testid="cal-month-title">
            <CalendarDays className="h-5 w-5 text-slate-400" /> {MONTH_NAMES[month]} {year}
          </h3>
          <div className="flex items-center gap-1">
            <button data-testid="cal-prev-btn" onClick={prevMonth} className="rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 active:scale-90"><ChevronLeft className="h-5 w-5" /></button>
            <button data-testid="cal-next-btn" onClick={nextMonth} className="rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 active:scale-90"><ChevronRight className="h-5 w-5" /></button>
          </div>
        </div>
        <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50/60">
          {DOW.map((d) => <div key={d} className="py-2 text-center text-[11px] font-extrabold tracking-wider text-slate-400">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((d, i) => {
            const events = d ? byDate[dateKey(d)] || [] : [];
            return (
              <div key={i} data-testid={d ? `cal-day-${dateKey(d)}` : `cal-empty-${i}`} className={`min-h-24 border-b border-r border-slate-100 p-1.5 ${d ? "" : "bg-slate-50/40"} ${isToday(d) ? "bg-indigo-50/50" : ""}`}>
                {d && (
                  <>
                    <div className={`mb-1 text-xs font-bold ${isToday(d) ? "flex h-6 w-6 items-center justify-center rounded-full bg-indigo-600 text-white" : "text-slate-500"}`}>{d}</div>
                    <div className="space-y-1">
                      {events.map((ev) => (
                        <div key={ev.id} data-testid={`cal-event-${ev.id}`} className={`group flex items-start justify-between gap-1 rounded-md border px-1.5 py-1 text-[10px] font-bold leading-tight ${TYPE_COLORS[ev.type] || TYPE_COLORS["Diğer"]}`}>
                          <span>{ev.name}{ev.grade ? ` · ${ev.grade}. Sınıf` : ""}</span>
                          {isAdmin && (
                            <button data-testid={`cal-del-${ev.id}`} onClick={() => remove(ev.id)} className="shrink-0 opacity-0 transition group-hover:opacity-100" title="Sil">
                              <Trash2 className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
